const PLACEHOLDER_VALUES = new Set(['', '—', '-', 'unknown']);

function cleanSessionValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const text = String(value).trim();
  if (!text || PLACEHOLDER_VALUES.has(text.toLowerCase())) {
    return '';
  }
  return text;
}

function pickSessionField(session, ...keys) {
  for (const key of keys) {
    const value = cleanSessionValue(session[key]);
    if (value) {
      return value;
    }
  }
  return '';
}

function formatOidcLogin(value) {
  if (value === true) {
    return 'Yes';
  }
  if (value === false) {
    return 'No';
  }
  return '';
}

function sessionDedupeKey(session) {
  return [
    session.location_ip,
    session.login_time,
    session.login_device,
    session.last_visit,
    session.active_location,
  ].join('|');
}

function parseUaHints() {
  const ua = navigator.userAgent || '';
  let browser = '';
  if (/Edg\//i.test(ua)) browser = 'edge';
  else if (/Firefox\//i.test(ua)) browser = 'firefox';
  else if (/Chrome\//i.test(ua) && !/Chg/i.test(ua)) browser = 'chrome';
  else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'safari';
  else if (/OPR\//i.test(ua)) browser = 'opera';

  let os = '';
  if (/Windows/i.test(ua)) os = 'windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'mac';
  else if (/Android/i.test(ua)) os = 'android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'ios';
  else if (/Linux/i.test(ua)) os = 'linux';

  const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  return { browser, os, mobile };
}

export function deviceMatchesActiveDevice(activeDevice) {
  const text = (activeDevice || '').toLowerCase();
  if (!text) return false;
  const { browser, os, mobile } = parseUaHints();
  if (browser && !text.includes(browser)) return false;
  if (os === 'linux' && !text.includes('linux') && !text.includes('gnu/linux')) return false;
  if (os && os !== 'linux' && !text.includes(os)) return false;
  if (mobile && !text.includes('mobile')) return false;
  if (!mobile && text.includes('mobile') && !text.includes('tablet')) return false;
  return Boolean(browser);
}

/**
 * Maps `/api/user/details` → `sessions[]` into UI rows (field names match API).
 */
export function mapApiSessionsToRows(apiSessions) {
  if (!Array.isArray(apiSessions) || apiSessions.length === 0) {
    return [];
  }

  const byKey = new Map();
  for (const session of apiSessions) {
    const key = sessionDedupeKey(session);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, session);
      continue;
    }
    if (session.is_current && !existing.is_current) {
      byKey.set(key, session);
    } else if (session.is_current && existing.is_current) {
      byKey.set(key, { ...existing, is_current: true });
    }
  }
  const unique = Array.from(byKey.values());

  const useApiCurrentFlag = unique.some((s) => typeof s.is_current === 'boolean');

  let currentIndex = -1;
  if (!useApiCurrentFlag) {
    currentIndex = unique.findIndex((s) =>
      deviceMatchesActiveDevice(
        pickSessionField(s, 'active_device', 'login_device'),
      ),
    );
    if (currentIndex < 0) {
      currentIndex = 0;
    }
  }

  const displayOrDash = (value) => value || '—';

  return unique.map((session, index) => {
    const location_ip = displayOrDash(
      pickSessionField(session, 'location_ip', 'ip_address', 'ip', 'client_ip'),
    );
    const active_location = displayOrDash(
      pickSessionField(
        session,
        'active_location',
        'location',
        'login_location',
        'geo_location',
      ),
    );
    const login_device = displayOrDash(
      pickSessionField(session, 'login_device', 'device'),
    );
    const login_time = displayOrDash(
      pickSessionField(session, 'login_time', 'logged_in_at'),
    );
    const last_visit = displayOrDash(
      pickSessionField(session, 'last_visit', 'last_active', 'last_activity'),
    );
    const oidc_login = displayOrDash(formatOidcLogin(session.oidc_login));
    const active_device = displayOrDash(
      pickSessionField(session, 'active_device'),
    );

    return {
      session_id: sessionDedupeKey(session),
      location_ip,
      active_location,
      login_device,
      login_time,
      oidc_login,
      last_visit,
      active_device,
      is_current: useApiCurrentFlag
        ? Boolean(session.is_current)
        : index === currentIndex,
      // Aliases used by older UI snippets
      location: active_location,
      ip_address: location_ip,
    };
  });
}

export function sessionPrimaryLabel(row) {
  return pickSessionField(row, 'login_device', 'active_device') || 'Unknown device';
}

export function sessionSecondaryLine(row) {
  const parts = [
    pickSessionField(row, 'active_location', 'location'),
    pickSessionField(row, 'location_ip', 'ip_address'),
  ].filter(Boolean);
  return parts.join(' · ');
}

/** Labels for profile / logout dialog (API field order). */
export const SESSION_DETAIL_FIELDS = [
  ['Login device', 'login_device'],
  ['IP address', 'location_ip'],
  ['Login location', 'active_location'],
  ['Login time', 'login_time'],
  ['Last visit', 'last_visit'],
];
